import type { CtxFlagId, Rule, RuleSchema, Visibility, VisibilityPredicateId } from './grammar';
import type { Decisions, RequirementId } from './vocabulary';

/**
 * Single source of truth for DG form sections.
 * Adding a new section here automatically populates SECTION_FIELDS and the
 * empty-set default in `mergeExtras`.
 */
export const DG_SECTION_IDS = [
  'identification',
  'classification',
  'weight',
  'flashpoint',
  'temperatureControl',
  'marinePollutant',
  'fireworks',
  'radionuclide',
  'fumigation',
  'endOfHoldingTime',
  'packaging',
  'carbonOfOrganicOrigin',
  'segregationGroups',
  'emergencyContact',
] as const;

export type DGSectionId = (typeof DG_SECTION_IDS)[number];

const requires = (key: RequirementId): Visibility => ({ kind: 'requires', requires: key });
const ctxFlag = (id: CtxFlagId): Visibility => ({ kind: 'ctxFlag', ctxFlag: id });
const named = (id: VisibilityPredicateId): Visibility => ({ kind: 'named', named: id });
const not = (v: Visibility): Visibility => ({ kind: 'not', not: v });
const all = (vs: Visibility[]): Visibility => ({ kind: 'all', all: vs });
const any = (vs: Visibility[]): Visibility => ({ kind: 'any', any: vs });
const allBlank = (paths: string[]): Visibility => ({ kind: 'allBlank', allBlank: paths });
const always = all([]);

const notBypassed: Visibility[] = [
  not(ctxFlag('isCoolantOrConditioner')),
  not(ctxFlag('isEmptyUncleaned')),
];

// Net Weight ↔ Capacity rescue: the parent error fires when both are blank
// AND the substance allows the swap; each leaf's own error fires only when
// the rescue doesn't apply, otherwise we'd double-emit on the same blank.
// `emitRescue` is true for one leaf only so the rescue error reports once.
function netOrCapacityRules(
  requiresLeaf: RequirementId,
  leafMessage: string,
  emitRescue: boolean
): Rule[] {
  const noRescueOrBothBlank = any([
    not(requires('requiresNetWeightOrCapacity')),
    allBlank(['netWeight', 'capacity']),
  ]);
  const leafGate = all([requires(requiresLeaf), ...notBypassed, noRescueOrBothBlank]);
  const rescueRules: Rule[] = emitRescue
    ? [
        {
          kind: 'when',
          condition: all([
            requires('requiresNetWeightOrCapacity'),
            ...notBypassed,
            allBlank(['netWeight', 'capacity']),
          ]),
          rule: { kind: 'nonBlank', message: 'Net Weight or Capacity is required' },
        },
      ]
    : [];
  return [
    ...rescueRules,
    { kind: 'when', condition: leafGate, rule: { kind: 'nonBlank', message: leafMessage } },
    { kind: 'when', condition: leafGate, rule: { kind: 'positiveNumber', message: leafMessage } },
    { kind: 'positiveNumber', message: 'Must be a positive number' },
  ];
}

export const DECISIONS: Decisions = {
  technicalName: ['CFR49', 'IMDG'],
};

// See CONTEXT.md "Rule schema" for grammar, named predicates, and severity rules.
export function buildDetailsAndWeightsTree() {
  return {
    rowChecks: {
      kind: 'group',
      visible: always,
      children: {
        unNumber: {
          kind: 'field',
          errorKey: 'unNumber',
          label: 'UN Number',
          section: 'identification',
          visible: always,
          valuePath: 'unNumber',
          severityLocked: true,
          rules: [{ kind: 'regex', pattern: '^\\d{4}$', message: 'Must be exactly 4 digits' }],
        },
        hazardClass: {
          kind: 'field',
          errorKey: 'hazardClass',
          label: 'Hazard Class',
          section: 'classification',
          visible: always,
          valuePath: 'hazardClass',
          severityLocked: true,
          rules: [{ kind: 'nonBlank', message: 'Hazard Class is required' }],
        },
        properShippingName: {
          kind: 'field',
          errorKey: 'properShippingName',
          label: 'Proper Shipping Name',
          section: 'identification',
          visible: always,
          valuePath: 'properShippingName',
          severityLocked: true,
          rules: [{ kind: 'nonBlank', message: 'Proper Shipping Name is required' }],
        },
        // Visibility kept `always` — the rule itself short-circuits when no
        // segregation groups are required (preserves current observable
        // behavior; no extra named visibility predicate needed).
        additionalSegregationGroups: {
          kind: 'field',
          errorKey: 'additionalSegregationGroups',
          label: 'Segregation Groups',
          section: 'segregationGroups',
          visible: always,
          valuePath: 'additionalSegregationGroups',
          severityLocked: true,
          rules: [{ kind: 'named', id: 'validateSegregationGroups' }],
        },
        substanceId: {
          kind: 'field',
          errorKey: 'substanceId',
          label: 'Substance Selection',
          section: 'identification',
          visible: named('multipleMatchesVisible'),
          valuePath: { named: 'substanceIdValue' },
          severityLocked: true,
          rules: [{ kind: 'nonBlank', message: 'Select a substance variant' }],
        },
      },
    },

    imdgNonFumigation: {
      kind: 'group',
      visible: all([ctxFlag('hasRequirements'), not(requires('requiresFumigationDetails'))]),
      children: {
        flashpoint: {
          kind: 'field',
          errorKey: 'flashpoint',
          label: 'Flashpoint',
          section: 'flashpoint',
          visible: requires('requiresFlashpoint'),
          valuePath: 'flashpoint.temperature.value',
          rules: [{ kind: 'nonBlank', message: 'Flashpoint is required for this cargo' }],
        },
        temperatureControl: {
          kind: 'group',
          visible: any([
            requires('requiresTemperatureControl'),
            requires('requiresStabilized'),
            ctxFlag('isStabilized'),
          ]),
          children: {
            controlTemperature: {
              kind: 'field',
              errorKey: 'controlTemperature',
              label: 'Control Temperature',
              section: 'temperatureControl',
              visible: all([
                requires('requiresTemperatureControl'),
                not(ctxFlag('isStabilizedNotByTC')),
              ]),
              valuePath: 'controlTemperature.value',
              rules: [
                { kind: 'nonBlank', message: 'Control Temperature is required for this cargo' },
              ],
            },
            emergencyTemperature: {
              kind: 'field',
              errorKey: 'emergencyTemperature',
              label: 'Emergency Temperature',
              section: 'temperatureControl',
              visible: all([
                requires('requiresTemperatureControl'),
                not(ctxFlag('isStabilizedNotByTC')),
              ]),
              valuePath: 'emergencyTemperature.value',
              rules: [
                { kind: 'nonBlank', message: 'Emergency Temperature is required for this cargo' },
              ],
            },
          },
        },
        // Intentional collision with imdgTechnicalName below: both leaves
        // write to errorKey 'technicalName' (the same form field), under
        // alternate substance-driven visibility paths. validator dedupes
        // first-error-wins. See CONTEXT.md "Canonical field identity".
        marinePollutantChemicalName: {
          kind: 'field',
          errorKey: 'technicalName',
          label: 'Technical Name',
          section: 'marinePollutant',
          visible: all([
            requires('requiresMarinePollutantChemicalNameIfDeclaredMP'),
            ctxFlag('isMarinePollutant'),
          ]),
          valuePath: { orFallback: ['technicalOrChemicalName', 'technicalName'] },
          rules: [
            {
              kind: 'nonBlank',
              message: 'Marine Pollutant Chemical Name is required for this substance',
            },
          ],
        },
        imdgTechnicalName: {
          kind: 'field',
          errorKey: 'technicalName',
          label: 'Technical Name',
          section: 'identification',
          visible: requires('technicalName'),
          valuePath: { orFallback: ['technicalOrChemicalName', 'technicalName'] },
          rules: [{ kind: 'nonBlank', message: 'Technical name is required for this substance' }],
        },
      },
    },

    packaging: {
      kind: 'group',
      visible: not(ctxFlag('isTankUnit')),
      children: {
        outerPackagingCode: {
          kind: 'field',
          owner: 'be',
          errorKey: 'outerPackaging',
          label: 'Outer Packaging',
          section: 'packaging',
          visible: always,
          valuePath: { orFallback: ['outerPackaging.code', 'outerPackaging.description'] },
        },
        outerPackagingAmount: {
          kind: 'field',
          owner: 'be',
          errorKey: 'outerPackagingCount',
          label: 'Outer Packaging Count',
          section: 'packaging',
          visible: always,
          valuePath: 'outerPackaging.count',
        },
        innerPackagingCode: {
          kind: 'field',
          owner: 'be',
          errorKey: 'innerPackaging',
          label: 'Inner Packaging',
          section: 'packaging',
          visible: always,
          valuePath: { orFallback: ['innerPackaging.code', 'innerPackaging.description'] },
        },
        innerPackagingAmount: {
          kind: 'field',
          owner: 'be',
          errorKey: 'innerPackagingCount',
          label: 'Inner Packaging Count',
          section: 'packaging',
          visible: always,
          valuePath: 'innerPackaging.count',
        },
        carbonOfOrganicOrigin: {
          kind: 'group',
          visible: requires('requiresCarbonOfOrganicOriginDeclarationDetails'),
          children: {
            dateOfPacking: {
              kind: 'field',
              errorKey: 'dateOfPacking',
              label: 'Date of Packing',
              section: 'carbonOfOrganicOrigin',
              visible: always,
              valuePath: 'carbonOfOrganicOriginDeclarationDetails.dateOfPacking',
              rules: [{ kind: 'nonBlank', message: 'Date Of Packing is required' }],
            },
            dateOfProduction: {
              kind: 'field',
              errorKey: 'dateOfProduction',
              label: 'Date of Production',
              section: 'carbonOfOrganicOrigin',
              visible: always,
              valuePath: 'carbonOfOrganicOriginDeclarationDetails.dateOfProduction',
              rules: [{ kind: 'nonBlank', message: 'Date Of Production is required' }],
            },
            temperature: {
              kind: 'field',
              errorKey: 'carbonOfOrganicOriginTemperature',
              label: 'Temperature on Packing',
              section: 'carbonOfOrganicOrigin',
              visible: always,
              valuePath: 'carbonOfOrganicOriginDeclarationDetails.temperatureOnDateOfPacking',
              rules: [{ kind: 'nonBlank', message: 'Temperature is required' }],
            },
          },
        },
      },
    },

    imdgExtras: {
      kind: 'group',
      visible: ctxFlag('hasRequirements'),
      children: {
        fireworks: {
          kind: 'field',
          errorKey: 'fireworkClassificationCode',
          label: 'Firework Classification Code',
          section: 'fireworks',
          visible: requires('requiresFireworkClassificationReference'),
          valuePath: 'fireworkClassificationCode',
          rules: [
            { kind: 'nonBlank', message: 'Firework Classification Code is required for Fireworks' },
          ],
        },
        radionuclide: {
          kind: 'group',
          visible: requires('requiresRadionuclideDetails'),
          children: {
            radionuclide: {
              kind: 'field',
              errorKey: 'radionuclide',
              label: 'Radionuclide',
              section: 'radionuclide',
              visible: always,
              valuePath: 'radioactivityDetails.radionuclide',
              rules: [{ kind: 'nonBlank', message: 'Radionuclide is required' }],
            },
            descriptionOfMaterial: {
              kind: 'field',
              errorKey: 'descriptionOfMaterial',
              label: 'Description of Material',
              section: 'radionuclide',
              visible: always,
              valuePath: 'radioactivityDetails.descriptionOfRadioactiveMaterial',
              rules: [{ kind: 'nonBlank', message: 'Material description is required' }],
            },
            radioactivity: {
              kind: 'field',
              errorKey: 'radioactivity',
              label: 'Radioactivity',
              section: 'radionuclide',
              visible: always,
              valuePath: 'radioactivityDetails.radioactivity',
              rules: [{ kind: 'nonBlank', message: 'Radioactivity is required' }],
            },
            radioactiveCategory: {
              kind: 'field',
              errorKey: 'radioactiveCategory',
              label: 'Radioactive Category',
              section: 'radionuclide',
              visible: always,
              valuePath: 'radioactivityDetails.radioactiveCategory',
              rules: [{ kind: 'nonBlank', message: 'Category is required' }],
            },
            transportIndex: {
              kind: 'field',
              errorKey: 'transportIndex',
              label: 'Transport Index',
              section: 'radionuclide',
              visible: always,
              valuePath: 'radioactivityDetails.transportIndex',
              rules: [{ kind: 'nonBlank', message: 'Transport index is required' }],
            },
            criticalitySafetyIndex: {
              kind: 'field',
              errorKey: 'criticalitySafetyIndex',
              label: 'Criticality Safety Index',
              section: 'radionuclide',
              visible: requires('requiresCriticalitySafetyIndex'),
              valuePath: 'radioactivityDetails.criticalitySafetyIndex',
              rules: [{ kind: 'nonBlank', message: 'Criticality Safety Index is required' }],
            },
          },
        },
      },
    },

    weights: {
      kind: 'group',
      visible: always,
      children: {
        netWeight: {
          kind: 'field',
          errorKey: 'netWeight',
          label: 'Net Weight',
          section: 'weight',
          visible: always,
          valuePath: 'netWeight',
          rules: netOrCapacityRules(
            'requiresNetWeight',
            'Weight must be declared on solids and explosive substances',
            true
          ),
        },
        capacity: {
          kind: 'field',
          errorKey: 'capacity',
          label: 'Capacity',
          section: 'weight',
          visible: always,
          valuePath: 'capacity',
          rules: netOrCapacityRules(
            'requiresCapacity',
            'Capacity must be declared on liquid substances',
            false
          ),
        },
        // First-error-wins order: nonBlank → compare gte netWeight → positiveNumber.
        // positiveNumber catches the netWeight-blank case that compare lets through.
        grossWeight: {
          kind: 'field',
          errorKey: 'grossWeight',
          label: 'Gross Weight',
          section: 'weight',
          visible: always,
          valuePath: 'grossWeight',
          rules: [
            { kind: 'nonBlank', message: 'Gross weight is required' },
            {
              kind: 'compare',
              op: 'gte',
              otherPath: 'netWeight',
              message: 'Gross weight must exceed or be equal to net weight',
            },
            { kind: 'positiveNumber', message: 'Gross weight is required' },
          ],
        },
        nec: {
          kind: 'field',
          errorKey: 'netExplosiveContent',
          label: 'Net Explosive Content',
          section: 'weight',
          visible: requires('requiresNEC'),
          valuePath: 'netExplosiveContent',
          rules: [
            { kind: 'nonBlank', message: 'N.E.C is required for explosive cargo' },
            { kind: 'positiveNumber', message: 'N.E.C is required for explosive cargo' },
          ],
        },
        dsitCode: {
          kind: 'field',
          errorKey: 'dsitCode',
          label: 'DSIT Code',
          section: 'weight',
          visible: requires('requiresDSITCode'),
          valuePath: 'dsitCode',
          rules: [{ kind: 'nonBlank', message: 'DSIT Code is required' }],
        },
        actualDGWeight: {
          kind: 'field',
          errorKey: 'actualDgWeight',
          label: 'Actual DG Weight',
          section: 'weight',
          visible: requires('requiresActualDgWeight'),
          valuePath: 'actualDgWeight',
          rules: [
            { kind: 'nonBlank', message: 'Actual DG Content is required for this cargo' },
            { kind: 'positiveNumber', message: 'Actual DG Content is required for this cargo' },
          ],
        },
      },
    },

    fumigation: {
      kind: 'group',
      visible: all([ctxFlag('hasRequirements'), requires('requiresFumigationDetails')]),
      children: {
        fumigantName: {
          kind: 'field',
          errorKey: 'fumigantName',
          label: 'Fumigant Name',
          section: 'fumigation',
          visible: always,
          valuePath: 'fumigationDetails.fumigantName',
          rules: [{ kind: 'nonBlank', message: 'Fumigant name is required' }],
        },
        fumigationDateTime: {
          kind: 'field',
          errorKey: 'fumigationDateTime',
          label: 'Fumigation Date/Time',
          section: 'fumigation',
          visible: always,
          valuePath: 'fumigationDetails.fumigationDateTime',
          rules: [{ kind: 'nonBlank', message: 'Date and Time of fumigation is required' }],
        },
        fumigantDosage: {
          kind: 'field',
          errorKey: 'fumigantDosage',
          label: 'Fumigant Dosage',
          section: 'fumigation',
          visible: always,
          valuePath: 'fumigationDetails.fumigationDosageUsed',
          rules: [{ kind: 'nonBlank', message: 'Fumigant Dosage is required' }],
        },
      },
    },

    endOfHoldingTime: {
      kind: 'field',
      errorKey: 't75EndOfHoldingTime',
      label: 'End of Holding Time',
      section: 'endOfHoldingTime',
      visible: all([requires('requiresEndOfHoldingTime'), named('t75HoldingTime')]),
      valuePath: 't75EndOfHoldingTime',
      rules: [{ kind: 'nonBlank', message: 'End of Holding Time is required' }],
    },
  } as const satisfies RuleSchema<DGSectionId>;
}

export type DetailsAndWeightsTree = ReturnType<typeof buildDetailsAndWeightsTree>;
